// A2-582 (operator 2026-10-27: "nothing flipping works here"). The review
// screen used to choose its buttons by checking each card's flag name, in 11
// files, while the server's answer routes kept their own rules; the two
// drifted, so the screen offered answers the server refused. The server now
// sends every row's answers (row.answers) and each group's shared answers
// (g.bulk_answers), built from rule_data/flags.json, and accepts exactly
// those. This file only sorts what it is sent into where it is drawn; it
// names no flag.

// Answers that apply to a whole group at once, drawn above its rows.
export const GROUP_KINDS = new Set([
  "PICK_RATE", "GO_TO", "FIX_IN_FILE", "NO_WAY_OUT", "ONLINE_EVENT_CHOICE", "ADD_REGISTRATION",
]);
// Answers given for several ticked rows at once.
export const TICK_KINDS = new Set([
  "KEEP", "PICK_RATE", "CREDIT_NOTE_CHANNEL", "PICK_COUNTRY", "ESTABLISHMENT", "PROPERTY_LETTING",
  "LEAVE_OUT_COPY", "CUSTOMER_COUNTRY", "SOLD_AS", "BUSINESS_EVIDENCE", "CLEAR_DECISION",
]);

export const answerKey = (a) => JSON.stringify(
  Object.keys(a).filter((k) => k !== "flags" && k !== "rows" && k !== "channels").sort()
    .map((k) => [k, a[k]]),
);

/** A1-103 — a note (the server's tier "info"; an older response's audit_only):
 * nothing to tick, folded, not counted. The one test every screen uses. */
export const isNote = (g) => (g.tier ? g.tier === "info" : !!g.audit_only);

/** The group's shared answer of `kind` for this card, or null. */
export const groupAnswer = (g, kind, extra = {}) => (g.bulk_answers || []).find(
  (a) => a.kind === kind
    && (kind !== "KEEP" || (a.flags || []).includes(g.flag_type))
    && Object.entries(extra).every(([k, v]) => a[k] === v),
) || null;

/** The answers a row shows on this card. A2-637 Owner A (operator's Step 4
 * check 2026-11-02): which card draws an answer is the server's decision
 * (api/_review_answers.card_answers, over every card the row sits on), sent
 * as row.answers per card - this screen no longer borrows another card's
 * answer by what page it has loaded. Whole-group answers the group already
 * shows above its rows are not repeated on the row. */
export const rowAnswersOnCard = (row, g) => {
  const shown = new Set((g.bulk_answers || []).filter((a) => GROUP_KINDS.has(a.kind)).map(answerKey));
  const seen = new Set();
  return (row?.answers || []).filter((a) => {
    if (a.kind === "KEEP" && !(a.flags || []).includes(g.flag_type)) return false;
    const k = answerKey(a);
    if (shown.has(k) || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/** Does the row take `kind` on this card (for ticking it into a group answer)? */
export const rowTakes = (row, g, kind, extra = {}) => (row?.answers || []).some(
  (a) => a.kind === kind
    && (kind !== "KEEP" || (a.flags || []).includes(g.flag_type))
    && Object.entries(extra).every(([k, v]) => a[k] === v),
);

/** How much a card's rows offer, for choosing which card a shared row is
 * drawn on (reviewCanonical). A card whose OWN question the row answers on
 * it ranks highest (a number, a country, a channel...), then a card showing
 * a row answer to another card's question, then a rate pick, then a
 * blocking card's "keep as it is", then a note. An audit-trail card ranks
 * as a note unless it carries its own answer (A1-74; A2-484 review: an
 * answered "is this rent?" stays changeable on its audit card). */
export const answerRank = (g) => {
  const answers = (g.rows || []).flatMap((r) => rowAnswersOnCard(r, g)).concat(g.bulk_answers || []);
  const perRow = answers.filter((a) => a.kind !== "KEEP" && !GROUP_KINDS.has(a.kind));
  if (perRow.some((a) => (a.flags || []).includes(g.flag_type))) return 90;
  // A1-103 step 4 (gate 2026-10-31): a row answered here ("left out") stays
  // here to say so - it no longer takes the answer, so it would otherwise
  // jump to another card the moment it was answered.
  if ((g.rows || []).some((r) => r.left_out)) return 90;
  if (isNote(g)) return 20;
  if (perRow.length) return 85;
  if (answers.some((a) => a.kind === "PICK_RATE")) return 80;
  if (g.family === "B") return 50;
  return 20;
};
